import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Offline Cache Hook for Modal Data
 * 
 * Provides intelligent caching with:
 * - IndexedDB storage for persistence
 * - Network status detection
 * - Cache invalidation strategies
 * - Automatic sync when back online
 */

const DB_NAME = 'ModalResultsCache';
const DB_VERSION = 1;
const STORES = {
  ORDERS: 'orders',
  JOBS: 'jobs', 
  RESULTS: 'analysisResults',
  METADATA: 'cacheMetadata'
};

// Cache expiration times (in milliseconds)
const CACHE_EXPIRY = {
  ORDERS: 24 * 60 * 60 * 1000,    // 24 hours
  JOBS: 12 * 60 * 60 * 1000,      // 12 hours  
  RESULTS: 6 * 60 * 60 * 1000,    // 6 hours
  METADATA: 30 * 24 * 60 * 60 * 1000 // 30 days
};

class OfflineCacheManager {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores if they don't exist
        Object.values(STORES).forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'id' });
            
            // Add indexes for efficient querying
            if (storeName === STORES.ORDERS) {
              store.createIndex('userId', 'userId', { unique: false });
              store.createIndex('casefileId', 'casefileId', { unique: false });
              store.createIndex('createdAt', 'createdAt', { unique: false });
            } else if (storeName === STORES.JOBS) {
              store.createIndex('orderId', 'orderId', { unique: false });
              store.createIndex('status', 'status', { unique: false });
            } else if (storeName === STORES.RESULTS) {
              store.createIndex('jobId', 'jobId', { unique: false });
            }
            
            store.createIndex('cachedAt', 'cachedAt', { unique: false });
          }
        });
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  async set(storeName, key, data, metadata = {}) {
    const db = await this.ensureDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const cacheEntry = {
      id: key,
      data,
      cachedAt: Date.now(),
      expiresAt: Date.now() + (CACHE_EXPIRY[storeName.toUpperCase()] || CACHE_EXPIRY.METADATA),
      metadata: {
        version: 1,
        source: 'firestore',
        ...metadata
      }
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheEntry);
      request.onsuccess = () => resolve(cacheEntry);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, key) {
    const db = await this.ensureDB();
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }
        
        // Check if cache entry has expired
        if (result.expiresAt < Date.now()) {
          console.log(`🗑️ Cache expired for ${storeName}:${key}`);
          this.delete(storeName, key); // Clean up expired entry
          resolve(null);
          return;
        }
        
        resolve(result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getMany(storeName, keys) {
    const results = await Promise.all(
      keys.map(key => this.get(storeName, key))
    );
    return results.filter(Boolean);
  }

  async query(storeName, indexName, value) {
    const db = await this.ensureDB();
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(value);
      request.onsuccess = () => {
        const results = request.result.filter(entry => 
          entry.expiresAt > Date.now()
        );
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    const db = await this.ensureDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    const db = await this.ensureDB();
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async cleanup() {
    // Remove expired entries from all stores
    const db = await this.ensureDB();
    const now = Date.now();
    
    for (const storeName of Object.values(STORES)) {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const index = store.index('cachedAt');
      
      const request = index.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.expiresAt < now) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }
  }
}

// Singleton instance
const cacheManager = new OfflineCacheManager();

export const useOfflineCache = (userId) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheStats, setCacheStats] = useState({
    orders: 0,
    jobs: 0,
    results: 0,
    lastSync: null
  });
  
  const syncQueueRef = useRef([]);
  const isInitializedRef = useRef(false);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network connection restored');
      setIsOnline(true);
      processSyncQueue();
    };
    
    const handleOffline = () => {
      console.log('📴 Network connection lost - switching to offline mode');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize cache and cleanup on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        await cacheManager.initPromise;
        await cacheManager.cleanup();
        await updateCacheStats();
        isInitializedRef.current = true;
        console.log('💾 Offline cache initialized');
      } catch (error) {
        console.error('Failed to initialize offline cache:', error);
      }
    };
    
    initialize();
  }, []);

  const updateCacheStats = async () => {
    try {
      const [orders, jobs, results] = await Promise.all([
        cacheManager.query(STORES.ORDERS, 'userId', userId),
        cacheManager.query(STORES.JOBS, 'cachedAt', IDBKeyRange.lowerBound(0)),
        cacheManager.query(STORES.RESULTS, 'cachedAt', IDBKeyRange.lowerBound(0))
      ]);
      
      setCacheStats({
        orders: orders.length,
        jobs: jobs.length,
        results: results.length,
        lastSync: Date.now()
      });
    } catch (error) {
      console.error('Failed to update cache stats:', error);
    }
  };

  const processSyncQueue = async () => {
    if (!isOnline || syncQueueRef.current.length === 0) return;
    
    console.log(`🔄 Processing ${syncQueueRef.current.length} queued sync operations`);
    
    const queue = [...syncQueueRef.current];
    syncQueueRef.current = [];
    
    for (const operation of queue) {
      try {
        await operation();
      } catch (error) {
        console.error('Sync operation failed:', error);
        // Re-queue failed operations
        syncQueueRef.current.push(operation);
      }
    }
    
    await updateCacheStats();
  };

  // Cache operations
  const cacheOrders = useCallback(async (orders) => {
    if (!isInitializedRef.current) return;
    
    try {
      await Promise.all(
        orders.map(order => 
          cacheManager.set(STORES.ORDERS, order.id, order, {
            userId,
            casefileId: order.casefileId
          })
        )
      );
      console.log(`💾 Cached ${orders.length} orders`);
      await updateCacheStats();
    } catch (error) {
      console.error('Failed to cache orders:', error);
    }
  }, [userId]);

  const cacheJobs = useCallback(async (jobs) => {
    if (!isInitializedRef.current) return;
    
    try {
      await Promise.all(
        jobs.map(job => 
          cacheManager.set(STORES.JOBS, job.id, job, {
            orderId: job.orderId,
            status: job.status
          })
        )
      );
      console.log(`💾 Cached ${jobs.length} jobs`);
      await updateCacheStats();
    } catch (error) {
      console.error('Failed to cache jobs:', error);
    }
  }, []);

  const cacheResults = useCallback(async (results) => {
    if (!isInitializedRef.current) return;
    
    try {
      const resultEntries = Object.entries(results).map(([jobId, result]) => ({
        id: jobId,
        jobId,
        ...result
      }));
      
      await Promise.all(
        resultEntries.map(result => 
          cacheManager.set(STORES.RESULTS, result.id, result)
        )
      );
      console.log(`💾 Cached ${resultEntries.length} results`);
      await updateCacheStats();
    } catch (error) {
      console.error('Failed to cache results:', error);
    }
  }, []);

  // Retrieval operations
  const getCachedOrders = useCallback(async (filters = {}) => {
    if (!isInitializedRef.current) return [];
    
    try {
      let orders;
      if (filters.casefileIds && filters.casefileIds.length > 0) {
        // Get orders for specific casefiles
        const allOrders = await Promise.all(
          filters.casefileIds.map(casefileId => 
            cacheManager.query(STORES.ORDERS, 'casefileId', casefileId)
          )
        );
        orders = allOrders.flat();
      } else {
        // Get all orders for user
        orders = await cacheManager.query(STORES.ORDERS, 'userId', userId);
      }
      
      return orders.map(entry => entry.data).sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
    } catch (error) {
      console.error('Failed to get cached orders:', error);
      return [];
    }
  }, [userId]);

  const getCachedJobs = useCallback(async (orderIds) => {
    if (!isInitializedRef.current) return [];
    
    try {
      const allJobs = await Promise.all(
        orderIds.map(orderId => 
          cacheManager.query(STORES.JOBS, 'orderId', orderId)
        )
      );
      
      return allJobs.flat().map(entry => entry.data);
    } catch (error) {
      console.error('Failed to get cached jobs:', error);
      return [];
    }
  }, []);

  const getCachedResults = useCallback(async (jobIds) => {
    if (!isInitializedRef.current) return {};
    
    try {
      const results = await cacheManager.getMany(STORES.RESULTS, jobIds);
      const resultsMap = {};
      
      results.forEach(entry => {
        if (entry && entry.data) {
          resultsMap[entry.data.jobId] = entry.data;
        }
      });
      
      return resultsMap;
    } catch (error) {
      console.error('Failed to get cached results:', error);
      return {};
    }
  }, []);

  // Cache management
  const clearCache = useCallback(async () => {
    try {
      await Promise.all(
        Object.values(STORES).map(store => cacheManager.clear(store))
      );
      await updateCacheStats();
      console.log('🗑️ Cache cleared');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }, []);

  const queueForSync = useCallback((operation) => {
    syncQueueRef.current.push(operation);
    if (isOnline) {
      processSyncQueue();
    }
  }, [isOnline]);

  return {
    // State
    isOnline,
    cacheStats,
    
    // Cache operations
    cacheOrders,
    cacheJobs, 
    cacheResults,
    
    // Retrieval operations
    getCachedOrders,
    getCachedJobs,
    getCachedResults,
    
    // Management
    clearCache,
    queueForSync,
    
    // Utilities
    isInitialized: isInitializedRef.current
  };
};
